import React, { useCallback, useMemo, useState } from 'react';

import { Button, FontAwesomeIcon } from '@appcharge/shared-ui';

import {
  Offer,
  PopUpProductSequence,
  ProductsSequence,
  RollingOfferProductSequence
} from 'common/contracts';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from 'components/ui';
import { EToasterStatus, showToast } from 'utils/showToast';

interface ICheatsDialogProps {
  cheatsOfferId: string | null;
  productsSequence: Offer['productsSequence'];
  updateCollectedIndex: {
    mutate: (
      variables: {
        indexCollected: number;
        playerId: string;
        offerId: string | null;
      },
      options?: {
        onSuccess?: () => void;
        onError?: () => void;
      }
    ) => void;
  };
  onClose: () => void;
}

type CheatsSequenceItem =
  | ProductsSequence
  | PopUpProductSequence
  | RollingOfferProductSequence;

export const CheatsDialog = ({
  cheatsOfferId,
  productsSequence,
  updateCollectedIndex,
  onClose
}: ICheatsDialogProps): React.ReactElement | null => {
  const [playerId, setPlayerId] = useState('');
  const [subOffer, setSubOffer] = useState('');

  const subOfferOptions = useMemo(
    () =>
      (productsSequence as CheatsSequenceItem[]).map((seq) => ({
        value: String(seq.index),
        label: `Sub Offer #${seq.index}`
      })),
    [productsSequence]
  );

  const handleClose = useCallback((): void => {
    setPlayerId('');
    setSubOffer('');
    onClose();
  }, [onClose]);

  const handleRun = useCallback((): void => {
    updateCollectedIndex.mutate(
      {
        indexCollected: Number(subOffer) - 1,
        playerId,
        offerId: cheatsOfferId
      },
      {
        onSuccess: () => {
          showToast({
            message: 'Cheats successfully created',
            status: EToasterStatus.SUCCESS
          });
          handleClose();
        },
        onError: () => {
          showToast({
            message: 'Error creating cheats',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  }, [subOffer, playerId, cheatsOfferId, updateCollectedIndex, handleClose]);

  const handleOpenChange = useCallback(
    (open: boolean): void => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handlePlayerIdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setPlayerId(e.target.value);
    },
    []
  );

  const handleSubOfferChange = useCallback((value: string): void => {
    setSubOffer(value);
  }, []);

  const isRunDisabled = !playerId || !subOffer;

  return (
    <Dialog open={!!cheatsOfferId} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white p-0 max-w-[600px] rounded-lg">
        <DialogHeader className="flex flex-row items-center justify-between px-6 py-4 border-b border-base-border">
          <DialogTitle className="text-base-mutedForeground text-p-l font-normal">
            Cheats
          </DialogTitle>
          <div className="flex items-center">
            <div className="w-px h-[24px] bg-base-border mr-[16px]" />
            <FontAwesomeIcon
              icon="fa-regular fa-xmark"
              className="w-[12px] h-[14px] cursor-pointer text-base-foreground hover:text-base-mutedForeground transition-colors"
              onClick={handleClose}
            />
          </div>
        </DialogHeader>
        <div className="px-[84px] pt-[25px] pb-[40px]">
          <h2 className="text-[20px] font-semibold text-base-foreground mb-[6px]">
            Move Player to Sub Offer
          </h2>
          <p className="text-[16px] font-normal text-base-mutedForeground mb-[20px]">
            Enter Player ID and select the destination offer
          </p>

          <Input
            label="Player ID"
            placeholder="ID number"
            value={playerId}
            onChange={handlePlayerIdChange}
            className="mb-[20px]"
          />

          <div className="flex items-end gap-[8px]">
            <div className="flex-1">
              <InputSelectDropdown
                label="Move to"
                options={subOfferOptions}
                placeholder="Select sub offer"
                onOptionChange={handleSubOfferChange}
              />
            </div>
            <Button
              onClick={handleRun}
              disabled={isRunDisabled}
              className="w-[77px] h-[40px] bg-base-foreground hover:bg-base-foreground/90 text-white"
            >
              Run
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
